import { useEffect, useRef, useState } from 'react';
import { cerrarSesion, modoDev } from '../auth/msal';
import type { Sesion } from '../api/tipos';
import { Avatar } from './Avatar';
import { LogoMenu } from './Marca';

export type Vista = 'colaborador' | 'admin';

interface Props {
  sesion: Sesion;
  vista: Vista;
  onVista: (vista: Vista) => void;
}

const solapa = (activa: boolean) =>
  `rounded-[7px] px-4 py-1.5 font-titulo text-[13px] font-semibold tracking-[.2px] transition-colors ${
    activa ? 'bg-lima text-tinta' : 'text-tenue hover:text-texto'
  }`;

export function Encabezado({ sesion, vista, onVista }: Props) {
  const [abierto, setAbierto] = useState(false);
  const contenedor = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!abierto) return;
    const alTocarAfuera = (evento: MouseEvent) => {
      if (!contenedor.current?.contains(evento.target as Node)) setAbierto(false);
    };
    const alEscapar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') setAbierto(false);
    };
    document.addEventListener('mousedown', alTocarAfuera);
    document.addEventListener('keydown', alEscapar);
    return () => {
      document.removeEventListener('mousedown', alTocarAfuera);
      document.removeEventListener('keydown', alEscapar);
    };
  }, [abierto]);

  return (
    <header className="flex-none">
      <div className="flex h-16 items-center gap-3 bg-panel px-4 sm:gap-[18px] sm:px-7">
        <LogoMenu alto={32} />
        <div className="hidden h-[26px] w-px bg-white/15 sm:block" />
        <div className="hidden font-titulo text-[19px] font-extrabold tracking-[.2px] sm:block">
          App Eventos
        </div>

        <div className="flex-1" />

        {sesion.esAdmin && (
          <div className="flex gap-1 rounded-[10px] border border-white/10 bg-white/[.06] p-1">
            <button type="button" onClick={() => onVista('colaborador')} className={solapa(vista === 'colaborador')}>
              Colaborador
            </button>
            <button type="button" onClick={() => onVista('admin')} className={solapa(vista === 'admin')}>
              Administración
            </button>
          </div>
        )}

        <div ref={contenedor} className="relative pl-2">
          <button
            type="button"
            onClick={() => setAbierto((previo) => !previo)}
            aria-expanded={abierto}
            className={`flex items-center gap-2.5 rounded-full border py-1 pl-1 pr-2.5 transition-colors sm:gap-3 ${
              abierto ? 'border-white/20 bg-white/[.08]' : 'border-transparent hover:bg-white/5'
            }`}
          >
            <Avatar usuarioId={sesion.usuario.id} nombre={sesion.usuario.nombre} tamano={32} />
            <span className="hidden max-w-[160px] truncate text-[13px] text-suave md:block">
              {sesion.usuario.nombre}
            </span>
          </button>

          {abierto && (
            <>
              <div className="fixed inset-0 z-[70]" onClick={() => setAbierto(false)} />
              <div className="absolute right-0 top-[52px] z-[80] w-[298px] animate-aparecer overflow-hidden rounded-xl border border-white/[.13] bg-panel shadow-menu">
                <div className="flex items-center gap-3 px-3.5 py-4">
                  <Avatar usuarioId={sesion.usuario.id} nombre={sesion.usuario.nombre} tamano={44} />
                  <div className="min-w-0">
                    <div className="truncate text-[14.5px] font-semibold text-texto">
                      {sesion.usuario.nombre}
                    </div>
                    <div className="mt-0.5 truncate text-xs text-debil">{sesion.usuario.email}</div>
                    <div className="mt-1 text-xs text-cian">
                      {sesion.esAdmin ? 'Administrador de eventos' : 'Colaborador'}
                    </div>
                  </div>
                </div>

                {modoDev ? (
                  // Sin login de Microsoft no hay sesión que cerrar: el botón
                  // recargaría y volvería a entrar el mismo usuario ficticio.
                  <div className="border-t border-white/[.08] px-3.5 py-3 text-xs leading-relaxed text-ambar">
                    Modo sin login activo. Para cerrar sesión de verdad, poné{' '}
                    <code className="text-cian">VITE_AUTH_DEV=0</code> y{' '}
                    <code className="text-cian">AUTH_DEV=false</code>.
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => void cerrarSesion()}
                    className="w-full border-t border-white/[.08] px-3.5 py-3 text-left text-[13.5px] font-semibold text-coral transition-colors hover:bg-white/5"
                  >
                    Cerrar sesión
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="h-[3px] bg-gradient-to-r from-[#6AB333] via-[#1166B0] to-panel" />
    </header>
  );
}
